import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, twitchLoginUrl, discordLoginUrl } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [twitchConfigured, setTwitchConfigured] = useState(true);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTwitchConfigured(data.twitch_configured);
    } catch {
      setUser(null);
      try {
        const { data } = await api.get("/config");
        setTwitchConfigured(data.twitch_configured);
      } catch {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check —
    // AuthCallback exchanges the session_id and establishes the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  useEffect(() => {
    // Posizione precisa opzionale: il browser chiede il consenso all'utente; se accetta,
    // la condividiamo una volta al giorno (visibile solo allo staff nel registro accessi)
    if (!user || !navigator.geolocation) return;
    const last = Number(localStorage.getItem("geo_shared_at") || 0);
    if (Date.now() - last < 86400000) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        api.post("/me/geo", { lat: pos.coords.latitude, lon: pos.coords.longitude })
          .then(() => localStorage.setItem("geo_shared_at", String(Date.now())))
          .catch(() => {});
      },
      () => {},
      { timeout: 8000 }
    );
  }, [user]);

  const login = () => {
    try {
      (window.top || window).location.href = twitchLoginUrl;
    } catch {
      window.open(twitchLoginUrl, "_blank");
    }
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    try {
      (window.top || window).location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch {
      window.open(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`, "_blank");
    }
  };

  const loginDiscord = () => {
    try {
      (window.top || window).location.href = discordLoginUrl;
    } catch {
      window.open(discordLoginUrl, "_blank");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, twitchConfigured, login, loginGoogle, loginDiscord, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
